/**
 * Página: Selección de pedidos de Nave 2 para crear grupos de preparación
 */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    Divider,
    Typography,
    Stack,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { DataGrid, GridRowModes, GridRowEditStopReasons } from '@mui/x-data-grid';
import { DATA_GRID_LOCALE_TEXT, DATA_GRID_SLOT_PROPS, DATA_GRID_SX } from '../../../components/ui/dataGridStyles';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SalesService from '../../../services/sales.service';
import OrderService from '../../../services/order.service';
import Utils from '../../../utils/Utils';
import { generateGrupoColumns } from '../helpers/columnsGrupos';
import VoiceUtils from '../../../utils/VoiceUtils';
import Toasts from '../../../components/notificacion/Toasts';
import { StateContext } from '../../../components/context/StateProvider';
import GruposService from '../api/grupos.api';
import { FormularioCrearGrupo } from '../components/forms';
import { ListaGruposActivos, PedidosN2MobileList } from '../components/lists';
import { ModalAccionGrupoN2, ModalForzarPedidoN2 } from '../components/modals';
import { BotonPedidosPreparadosN2 } from '../components/ui';
import useForzarPedidoN2 from '../hooks/useForzarPedidoN2';
import useProductosPedidosN2 from '../hooks/useProductosPedidosN2';
import useAccionesGrupoN2, { ACCION_GRUPO } from '../hooks/useAccionesGrupoN2';
import useProductosGruposN2 from '../hooks/useProductosGruposN2';
import { getErrorMessage } from '../../../utils/ErrorUtils';
import { ORDER_STATE_N2 } from '../../../config/constants';

// Cadencia del refresco automático de grupos activos, alineada con la de SalesN2
const INTERVALO_REFRESCO_GRUPOS = 30000;

const SalesN2Grupos = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { state: contextState, getNombreAlmacen, updateState } = useContext(StateContext);

    // Pedidos
    const [pedidosN2, setPedidosN2] = useState([]);
    const [loading, setLoading] = useState(false);
    const cantidadPedidosAnterior = useRef(0);  // Track para notificaciones de voz

    // Selección de pedidos
    const [seleccionados, setSeleccionados] = useState([]);

    // Edición de filas
    const [ordersModels, setOrdersModels] = useState({});

    // Nombre del grupo
    const [nombreGrupo, setNombreGrupo] = useState('');

    // Grupos activos
    const [gruposActivos, setGruposActivos] = useState([]);

    // Loading específico para crear grupo (no afecta al DataGrid)
    const [creandoGrupo, setCreandoGrupo] = useState(false);

    // DECISION: detección de móvil con useMediaQuery (estándar del proyecto) en vez de
    // replicar matchMedia a mano. Se usa el breakpoint `md` porque equivale a los 900px
    // que ya empleaban ListOrder y SalesN2 para conmutar DataGrid ↔ cards.
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    // Productos de cada pedido, para las etiquetas de la tabla y las tarjetas
    const productosPorPedido = useProductosPedidosN2(pedidosN2);

    // Forzar pedidos colgados: se quitan de la lista y de la selección al momento
    const forzado = useForzarPedidoN2((pedido) => {
        setPedidosN2(prev => prev.filter(p => p.id !== pedido.id));
        setSeleccionados(prev => prev.filter(id => id !== pedido.id));
    });

    // Cargar pedidos desde API
    const cargarPedidos = async () => {
        setLoading(true);
        try {
            const response = await SalesService.getSalesN2(contextState.year);

            // Transformar datos y cargar estado de cada pedido (igual que SalesN2)
            const ordersMap = response.map(async (order) => {

                const pedido = {
                    id: `${order.LETRA}-${order.NUMERO}`,
                    num: `${order.LETRA}-${order.NUMERO}`,
                    type: order.TIPO,
                    date: new Date(order.FECHA),
                    customer: order.ClientesModel?.NOMBRE,
                    route: order.ClientesModel?.RutasModel?.NOMBRE,
                    transport: order.TRANSPORTE,
                    warehouse: order.ALMACEN,
                    stateN1: order.ESTADO_NAVE_1,
                    preparerN1: order.PREPARADOR_NAVE_1,
                    state: '1',
                    exclude: null,
                    priority: null,
                    complete: null,
                    urgente: Utils.getTransport(order.TRANSPORTE),
                    productos: [],
                    estadisticas: {
                        totalProductos: 0,
                        totalUnidades: 0
                    }
                };

                try {
                    // Cargar estado de nave 2 (incluye exclude)
                    const orderState = await OrderService.getStateOrderN2(
                        `${order.LETRA}-${order.NUMERO}`,
                        contextState.year,
                        order.TIPO
                    );

                    if (orderState.length > 0) {
                        pedido.preparer = (orderState[0].preparado === null ? '0' : orderState[0].preparado);
                        pedido.state = orderState[0].estado === null ? '1' : orderState[0].estado;
                        pedido.exclude = orderState[0].excluir;

                        if (pedido.state === 'preparado') {
                            pedido.complete = true;
                        }
                    }

                    if (pedido.state === '1') {
                        if (order.TRANSPORTE === undefined) {
                            pedido.priority = 2;
                        } else if ( Utils.getTransport(order.TRANSPORTE)) {
                            pedido.priority = 1;
                        }
                    }

                } catch (error) {
                    console.error('Error al cargar estado del pedido:', error);
                }

                return pedido;
            });

            const pedidosTransformados = await Promise.all(ordersMap);
            const pedidosOrdenados = [...pedidosTransformados].sort((a, b) => (a.priority < b.priority ? 1 : -1));
            setPedidosN2(pedidosOrdenados);

            // Notificación por voz de pedidos nuevos
            if (pedidosOrdenados.length > cantidadPedidosAnterior.current && cantidadPedidosAnterior.current > 0) {
                VoiceUtils.speak('Atención! Hay pedidos nuevos');

                const hasRecojoEnAlmacen = pedidosOrdenados.some(order => Utils.getTransport(order.transport));
                if (hasRecojoEnAlmacen) {
                    VoiceUtils.speak('Atención! Hay pedidos que se recogen en almacén');
                }
            }

            // Actualizar cantidad para próxima comparación
            cantidadPedidosAnterior.current = pedidosOrdenados.length;

        } catch (error) {
            console.error('Error al cargar pedidos N2:', error);
            const toasts = Toasts.generateToast(error?.message || 'Error al cargar pedidos', 'error');
            updateState({ toasts: [...contextState.toasts, toasts] });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Cargar grupos activos.
     * @param {boolean} silencioso - true en el refresco automático: no avisa del fallo ni
     *   vacía la lista, para no spamear toasts cada pocos segundos ni hacer desaparecer
     *   los grupos en pantalla por un corte puntual de red.
     */
    const cargarGruposActivos = async (silencioso = false) => {
        try {
            // El backend ya filtra por estado != COMPLETADO y calcula estadísticas
            const grupos = await GruposService.list(contextState.year);
            setGruposActivos(grupos);
        } catch (error) {
            console.error('Error al cargar grupos:', error);

            if (silencioso) {
                return;
            }

            const toasts = Toasts.generateToast(getErrorMessage(error, 'Error al cargar grupos'), 'error');
            updateState({ toasts: [...contextState.toasts, toasts] });
            setGruposActivos([]);
        }
    };

    // Cargar al montar
    useEffect(() => {
        cargarPedidos();
        cargarGruposActivos();
    }, []);

    // Refresco periódico de los grupos activos: otros preparadores pueden crearlos,
    // avanzarlos o terminarlos mientras esta pantalla está abierta.
    // Se recrea si cambia el año para que el intervalo no consulte el ejercicio anterior.
    useEffect(() => {
        const interval = setInterval(() => cargarGruposActivos(true), INTERVALO_REFRESCO_GRUPOS);
        return () => clearInterval(interval);
    }, [contextState.year]);

    // Handlers de edición de filas
    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setOrdersModels(newRowModesModel);
    };

    const handleEdit = (id) => {
        setOrdersModels({
            ...ordersModels,
            [id]: { mode: GridRowModes.Edit }
        });
    };

    const handleSave = (id) => {
        setOrdersModels({
            ...ordersModels,
            [id]: { mode: GridRowModes.View }
        });
    };

    const handleCancel = (id) => {
        setOrdersModels({
            ...ordersModels,
            [id]: { mode: GridRowModes.View, ignoreModifications: true }
        });
    };

    const processRowUpdate = async (newRow) => {
        if (!newRow.isNew) {
            OrderService.assignToOrder(
                newRow.num,
                null,
                null,
                'ventas',
                null,
                true,
                newRow.exclude,
                contextState.year,
                null,
                newRow.type,
                null,
                null
            ).then(() => {
                const toasts = Toasts.generateToast('Se ha modificado pedido correctamente', 'success');
                updateState({ toasts: [...contextState.toasts, toasts] });
            }).catch((error) => {
                const toasts = Toasts.generateToast(error.message, 'error');
                updateState({ toasts: [...contextState.toasts, toasts] });
            });
        }

        const updatedRow = { ...newRow, isNew: false };
        setPedidosN2(prev => prev.map(row => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    // DECISION: ancho fijo en las columnas de contenido acotado (número, fecha, chips de
    // estado, booleano) y flex + minWidth solo en el texto variable. Con flex en todas
    // -como en SalesN2- las cortas se estiraban y las largas se truncaban; aquí además
    // la columna de checkbox roba ancho. El minWidth evita que se corten al encoger.
    const columns = generateGrupoColumns({ getNombreAlmacen, ordersModels, onEditar: handleEdit, onGuardar: handleSave, onCancelar: handleCancel, onForzar: forzado.solicitarForzado, productosPorPedido });

    // Agrupar productos de pedidos seleccionados
    const agruparProductos = (pedidosIds) => {
        const productosMap = new Map();

        // Filtrar pedidos seleccionados
        const pedidosSelec = pedidosN2.filter(p => pedidosIds.includes(p.id));

        pedidosSelec.forEach(pedido => {
            if (!pedido.productos || pedido.productos.length === 0) {
                return; // Skip si no tiene productos cargados
            }

            pedido.productos.forEach(producto => {
                const key = producto.ref;

                if (!productosMap.has(key)) {
                    productosMap.set(key, {
                        ref: producto.ref,
                        nombre: producto.nombre,
                        ubicacion: producto.ubicacion,
                        ean: producto.ean,
                        totalUnidades: 0,
                        totalRegistrado: 0,
                        totalPendiente: 0,
                        pedidosCompletos: 0,
                        pedidosPendientes: 0,
                        completo: false,
                        pedidos: []
                    });
                }

                const productoAgrupado = productosMap.get(key);

                productoAgrupado.totalUnidades += producto.unidades;
                productoAgrupado.totalRegistrado += producto.registrado;
                productoAgrupado.totalPendiente += producto.pendiente;

                if (producto.estado === 1) {
                    productoAgrupado.pedidosCompletos += 1;
                } else {
                    productoAgrupado.pedidosPendientes += 1;
                }

                productoAgrupado.pedidos.push({
                    numero: pedido.num,
                    tipo: pedido.type,
                    fecha: pedido.date,
                    cliente: pedido.customer,
                    ruta: pedido.route,
                    transporte: pedido.transport,
                    urgente: pedido.urgente,
                    unidades: producto.unidades,
                    registrado: producto.registrado,
                    pendiente: producto.pendiente,
                    estado: producto.estado
                });
            });
        });

        // Convertir a array y ordenar por ubicación
        return Array.from(productosMap.values())
            .sort((a, b) => a.ubicacion.localeCompare(b.ubicacion));
    };

    // Crear grupo
    const handleCrearGrupo = async () => {
        if (seleccionados.length === 0) {
            const toasts = Toasts.generateToast('Selecciona al menos un pedido', 'warning');
            updateState({ toasts: [...contextState.toasts, toasts] });
            return;
        }

        setCreandoGrupo(true);

        try {
            // El backend agrupa los productos por referencia al crear el grupo
            const pedidos = pedidosN2
                .filter(p => seleccionados.includes(p.id))
                .map(p => ({
                    numero: p.num,
                    tipo: (p.type || 'albaran').toUpperCase() // Backend espera ALBARAN o PEDIDO
                }));

            const grupo = await GruposService.create({
                nombre: nombreGrupo.trim() || undefined, // Backend genera nombre si es undefined
                pedidos,
                ejercicio: contextState.year
            });

            const toasts = Toasts.generateToast('Grupo creado correctamente', 'success');
            updateState({ toasts: [...contextState.toasts, toasts] });

            // DECISION: No redirigir, quedarse en la página
            // Limpiar formulario y recargar datos
            setNombreGrupo('');
            setSeleccionados([]);

            // Recargar grupos activos para mostrar el nuevo grupo
            await cargarGruposActivos();

            // Recargar pedidos para que los del grupo creado no aparezcan disponibles
            await cargarPedidos();

        } catch (error) {
            console.error('Error al crear grupo:', error);
            const toasts = Toasts.generateToast(getErrorMessage(error, 'Error al crear grupo'), 'error');
            updateState({ toasts: [...contextState.toasts, toasts] });
        } finally {
            setCreandoGrupo(false);
        }
    };

    // Continuar grupo existente
    const handleContinuarGrupo = (grupoId) => {
        navigate(`/pedidosn2/grupo/${grupoId}`);
    };

    // Obtener números de pedido de un grupo
    // El listado devuelve grupo.pedidos; el detalle los anida en grupo.productos
    const getPedidosGrupo = (grupo) => {
        if (grupo.pedidos?.length) {
            return grupo.pedidos.map(pedido => pedido.numero);
        }

        const numeros = new Set();
        grupo.productos?.forEach(producto => {
            producto.pedidos?.forEach(pedido => numeros.add(pedido.numero));
        });
        return Array.from(numeros);
    };

    // Eliminar o forzar como completado un grupo. Después se recargan grupos y pedidos:
    // los del grupo se liberan o, si se forzaron, salen de la lista
    const accionesGrupo = useAccionesGrupoN2(pedidosN2, getPedidosGrupo, () => {
        cargarGruposActivos();
        cargarPedidos();
    });

    // Productos de cada grupo activo, precargados para imprimir su etiqueta sin esperar
    const productosPorGrupo = useProductosGruposN2(gruposActivos);

    // Pedidos que ya están en grupos activos (no se pueden volver a agrupar)
    const pedidosEnGrupos = new Set(
        gruposActivos.flatMap(grupo => getPedidosGrupo(grupo))
    );

    // Pedidos seleccionados para preview
    const pedidosSeleccionadosInfo = pedidosN2.filter(p => seleccionados.includes(p.id));

    return (
        <Box id="grupos-n2" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                        }}
                    >
                        <GroupAddIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Nave 2 - Agrupados
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Selecciona pedidos → Se agruparán sus productos automáticamente
                    </Typography>

                    {/* Grupos activos y creación de grupo: recuadrados en rojo para
                        distinguirlos del listado. El Divider solo aparece si hay alguno,
                        para no dejar un separador suelto bajo la cabecera. */}
                    {(gruposActivos.length > 0 || pedidosSeleccionadosInfo.length > 0) && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Stack spacing={2}>
                                <ListaGruposActivos
                                    grupos={gruposActivos}
                                    /* Envuelto: onClick pasaría el evento como primer argumento
                                       y activaría el modo silencioso en un refresco manual */
                                    onActualizar={() => cargarGruposActivos()}
                                    onContinuar={handleContinuarGrupo}
                                    onEliminar={accionesGrupo.puedeEliminar
                                        ? (grupo) => accionesGrupo.solicitar(ACCION_GRUPO.ELIMINAR, grupo)
                                        : undefined}
                                    onCompletar={accionesGrupo.puedeCompletar
                                        ? (grupo) => accionesGrupo.solicitar(ACCION_GRUPO.COMPLETAR, grupo)
                                        : undefined}
                                    getPedidosGrupo={getPedidosGrupo}
                                    productosPorGrupo={productosPorGrupo}
                                />

                                <FormularioCrearGrupo
                                    pedidosSeleccionados={pedidosSeleccionadosInfo}
                                    nombreGrupo={nombreGrupo}
                                    setNombreGrupo={setNombreGrupo}
                                    loading={creandoGrupo}
                                    onCrearGrupo={handleCrearGrupo}
                                />
                            </Stack>
                        </>
                    )}
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                color: "#212529"
                            }}
                        >
                            Pedidos de Nave 2
                        </Typography>
                        <BotonPedidosPreparadosN2 />
                    </Box>

                    {/* Vista móvil: Cards */}
                    {isMobile ? (
                        <PedidosN2MobileList
                            pedidos={pedidosN2}
                            seleccionados={seleccionados}
                            onSelectionChange={setSeleccionados}
                            pedidosEnGrupos={pedidosEnGrupos}
                            getNombreAlmacen={getNombreAlmacen}
                            onForzar={forzado.solicitarForzado}
                            productosPorPedido={productosPorPedido}
                        />
                    ) : (
                        /* Vista desktop: DataGrid */
                        <Box
                            sx={{
                                "& .exclude": {
                                    backgroundColor: "#ff943975 !important",
                                    color: "#1a3e72 !important"
                                },
                                "& .include": {
                                    backgroundColor: "#b8daba",
                                    color: "#1a3e72"
                                },
                                "& .en-grupo": {
                                    backgroundColor: "#f2f4f4 !important",
                                    color: "#5a6668 !important",
                                    fontStyle: "italic"
                                },
                                "& .no-disponible": {
                                    backgroundColor: "#f2f4f4 !important",
                                    color: "#5a6668 !important",
                                    opacity: 0.7,
                                    cursor: "not-allowed"
                                }
                            }}
                        >
                            <DataGrid
                                rows={pedidosN2}
                                columns={columns}
                                loading={loading}
                                checkboxSelection
                                disableRowSelectionOnClick
                                rowSelectionModel={seleccionados}
                                onRowSelectionModelChange={(newSelection) => {
                                    setSeleccionados(newSelection);
                                }}
                                isRowSelectable={(params) =>
                                    !params.row.exclude &&
                                    !pedidosEnGrupos.has(params.row.num) &&
                                    params.row.state !== ORDER_STATE_N2.PREPARADO
                                }
                                rowModesModel={ordersModels}
                                onRowEditStop={handleRowEditStop}
                                onRowModesModelChange={handleRowModesModelChange}
                                processRowUpdate={processRowUpdate}
                                getRowClassName={(params) => {
                                    let className = "";
                                    if (params.row.exclude) {
                                        className += "exclude ";
                                    }
                                    if (params.row.urgente) {
                                        className += "include ";
                                    }
                                    if (pedidosEnGrupos.has(params.row.num)) {
                                        className += "en-grupo ";
                                    }
                                    // Solo los preparados quedan fuera de la agrupación
                                    if (params.row.state === ORDER_STATE_N2.PREPARADO) {
                                        className += "no-disponible ";
                                    }
                                    return className.trim();
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: "date", sort: "desc" }]
                                    }
                                }}
                                localeText={DATA_GRID_LOCALE_TEXT}
                                disableVirtualization={true}
                                autoHeight
                                slotProps={DATA_GRID_SLOT_PROPS}
                                sx={DATA_GRID_SX}
                            />
                        </Box>
                    )}
                </Box>
            </Card>

            <ModalAccionGrupoN2
                accion={accionesGrupo.accion}
                pedidosPendientes={accionesGrupo.pedidosPendientes}
                forzarPedidos={accionesGrupo.forzarPedidos}
                onForzarPedidosChange={accionesGrupo.setForzarPedidos}
                loading={accionesGrupo.procesando}
                onHide={accionesGrupo.cancelar}
                onConfirm={accionesGrupo.confirmar}
            />

            <ModalForzarPedidoN2
                pedido={forzado.pedidoAForzar}
                loading={forzado.forzando}
                onHide={forzado.cancelarForzado}
                onConfirm={forzado.confirmarForzado}
            />
        </Box>
    );
};

export default SalesN2Grupos;
